import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useSingleTabEnforcement } from '../hooks/useSingleTabEnforcement'
import { useSessionReplacedPoll } from '../hooks/useSessionReplacedPoll'
import { useActivityTracker } from '../hooks/useActivityTracker'
import { SessionWarningModal } from './SessionWarningModal'
import type { TenantThemeConfig } from '../types/theme'
import ChatWidget from './ChatWidget'
import { getThemeAssetUrl } from '../utils/themeUtils'

// Extend JSX IntrinsicElements to include marquee
declare global {
  namespace JSX {
    interface IntrinsicElements {
      marquee: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        direction?: string
        onMouseOver?: (event: React.MouseEvent<HTMLElement>) => void
        onMouseOut?: (event: React.MouseEvent<HTMLElement>) => void
      }
    }
  }
}

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface AuthenticatedLayoutProps {
  children: React.ReactNode
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({ children, themeConfig, assetConfig }) => {
  // Use theme-specific assets path based on themeConfig
  const getAssetUrl = (path: string) => {
    return getThemeAssetUrl(assetConfig.CDN_BASE_URL, themeConfig.themeId, path)
  }
  
  // Extract configuration from themeConfig
  const { branding } = themeConfig.theme
  const { marqueeText } = themeConfig.settings
  
  // Menu state
  const [showUserMenu, setShowUserMenu] = useState(false)
  
  // Get authenticated user data and logout function
  const navigate = useNavigate()
  const location = useLocation()
  const accessAlertKeyRef = useRef<string | null>(null)
  const { user, logout, isAuthenticated } = useAuth()
  const { walletData, isLoadingWallet, formatCurrency } = useWallet()
  const { userInfo, isLoadingUserInfo, getPrimaryBankAccount } = useUserInfo()
  const { isDuplicateTab, takeOverSession } = useSingleTabEnforcement(!!isAuthenticated)
  useSessionReplacedPoll(!!isAuthenticated)

  // Idle logout: เหมือน user-modern — เรียกใน layout ที่ mount เมื่อล็อกอินแล้ว จึงเริ่ม timer ได้ถูกต้อง
  const sessionIdleMinutes = (typeof themeConfig.settings?.sessionIdleMinutes === 'number' && themeConfig.settings.sessionIdleMinutes >= 0 && themeConfig.settings.sessionIdleMinutes <= 120)
    ? themeConfig.settings.sessionIdleMinutes
    : 30
  const activityTrackingEnabled = sessionIdleMinutes > 0
  const {
    showSessionWarning,
    timeRemaining,
    extendSession,
    handleLogout: sessionTimeoutLogout,
  } = useActivityTracker({
    inactivityTimeout: sessionIdleMinutes * 60 * 1000,
    warningTimeout: 5 * 60 * 1000,
    heartbeatInterval: 10 * 60 * 1000,
    enabled: activityTrackingEnabled,
  })

  useEffect(() => {
    if (!isAuthenticated) {
      accessAlertKeyRef.current = null
      return
    }
    if (!userInfo) return

    const path = location.pathname || '/'
    const serviceConfigs = Array.isArray(userInfo.serviceConfigs) ? userInfo.serviceConfigs : []
    const isServiceEnabled = (serviceType: 'lottery' | 'casino') => {
      const target = serviceConfigs.find((item: any) => item?.serviceType?.toLowerCase?.() === serviceType)
      return target ? target.isActive !== false : true
    }

    const isLotteryPath = [
      '/lotto',
      '/lottery-terms',
      '/lottery-bet',
      '/set',
      '/set-terms',
      '/set-lotto-game',
      '/yeekee',
      '/yeekee-terms',
    ].some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
    const isCasinoPath = path === '/casino' || path === '/lobby-casino'

    // ปิดทั้งระบบระดับ tenant (ปิดปรับปรุงระบบ) — มาก่อนสิทธิ์ระดับ user
    const tenantLotteryEnabled = themeConfig?.settings?.lotteryEnabled !== false
    const tenantCasinoEnabled = themeConfig?.settings?.casinoEnabled !== false

    let blockedService: 'lottery' | 'casino' | null = null
    let blockedReason: 'tenant' | 'user' = 'user'
    if (isLotteryPath) {
      if (!tenantLotteryEnabled) {
        blockedService = 'lottery'
        blockedReason = 'tenant'
      } else if (!isServiceEnabled('lottery')) {
        blockedService = 'lottery'
        blockedReason = 'user'
      }
    } else if (isCasinoPath) {
      if (!tenantCasinoEnabled) {
        blockedService = 'casino'
        blockedReason = 'tenant'
      } else if (!isServiceEnabled('casino')) {
        blockedService = 'casino'
        blockedReason = 'user'
      }
    }

    if (!blockedService) {
      accessAlertKeyRef.current = null
      return
    }

    const alertKey = `${blockedService}:${blockedReason}:${path}`
    if (accessAlertKeyRef.current === alertKey) return
    accessAlertKeyRef.current = alertKey

    void Swal.fire({
      icon: 'warning',
      title: blockedReason === 'tenant'
        ? 'ปิดปรับปรุงระบบ'
        : (blockedService === 'lottery' ? 'ปิดการใช้งานหวย' : 'ปิดการใช้งานเกมส์'),
      text:
        blockedReason === 'tenant'
          ? (blockedService === 'lottery'
              ? 'ขณะนี้ระบบหวยปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง'
              : 'ขณะนี้ระบบเกมส์ปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง')
          : (blockedService === 'lottery'
              ? 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานหวยชั่วคราว กรุณาติดต่อแอดมิน'
              : 'บัญชีนี้ถูกปิดสิทธิ์การเข้าใช้งานเกมส์ชั่วคราว กรุณาติดต่อแอดมิน'),
      confirmButtonText: 'ตกลง',
    }).finally(() => {
      navigate('/dashboard', { replace: true })
    })
  }, [isAuthenticated, location.pathname, navigate, userInfo, themeConfig?.settings?.lotteryEnabled, themeConfig?.settings?.casinoEnabled])

  // Check for concurrent login detection
  useEffect(() => {
    const checkConcurrentLogin = () => {
      // Check if there's a stored flag indicating concurrent login
      const concurrentLoginFlag = sessionStorage.getItem('concurrentLoginDetected')
      if (concurrentLoginFlag) {
        sessionStorage.removeItem('concurrentLoginDetected')
        
        // Show countdown alert
        let countdown = 5
        Swal.fire({
          icon: 'warning',
          title: 'มีการเข้าสู่ระบบจากอุปกรณ์อื่น',
          html: `
            <div class="text-center">
              <p class="mb-2">บัญชีของคุณได้เข้าสู่ระบบจากอุปกรณ์อื่นแล้ว</p>
              <p class="mb-0">กำลังออกจากระบบ ใน <span id="logoutCountdown" class="fw-bold text-danger">${countdown}</span> วินาที</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          timer: 5000,
          timerProgressBar: true,
          willClose: () => {
            // Force logout current session
            handleForcedLogout()
          }
        })

        // Update countdown
        const countdownInterval = setInterval(() => {
          countdown--
          const countdownElement = document.getElementById('logoutCountdown')
          if (countdownElement && countdown > 0) {
            countdownElement.textContent = countdown.toString()
          }
          if (countdown <= 0) {
            clearInterval(countdownInterval)
          }
        }, 1000)
      }
    }

    checkConcurrentLogin()
  }, [])

  // User data from authentication and wallet
  const userData = {
    username: user?.username || 'ผู้ใช้',
    balance: walletData ? formatCurrency(walletData.balance.main) : (isLoadingWallet ? 'กำลังโหลด...' : '0.00')
  }

  const handleForcedLogout = () => {
    // Force clear all auth data
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('deviceId')
    localStorage.removeItem('rememberMe')
    sessionStorage.clear()
    
    // Reload page to reset state
    window.location.reload()
  }

  const handleLogout = async () => {
    try {
      // แสดง confirmation dialog
      const result = await Swal.fire({
        icon: 'question',
        title: 'ออกจากระบบ',
        text: 'คุณต้องการออกจากระบบใช่หรือไม่?',
        showCancelButton: true,
        confirmButtonText: 'ออกจากระบบ',
        cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d'
      })

      if (result.isConfirmed) {
        // แสดง loading
        Swal.fire({
          title: 'กำลังออกจากระบบ...',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading()
          }
        })

        // ปิด user menu
        setShowUserMenu(false)
        
        try {
          // เรียก logout จาก AuthContext
          await logout()
          
          // แสดง success message
          Swal.fire({
            icon: 'success',
            title: 'ออกจากระบบเรียบร้อย',
            text: 'ขอบคุณที่ใช้บริการ',
            timer: 2000,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false
          })
          
          // Redirect หลังจาก 2 วินาที
          setTimeout(() => {
            window.location.href = '/'
          }, 2000)
          
        } catch (logoutError) {
          console.error('❌ Logout error:', logoutError)
          
          // แม้ว่า API call จะ fail ก็ยังต้อง clear local state
          Swal.fire({
            icon: 'warning',
            title: 'ออกจากระบบบางส่วน',
            text: 'ไม่สามารถออกจากระบบจากเซิร์ฟเวอร์ได้ แต่ได้ลบข้อมูลในเครื่องแล้ว',
            timer: 3000,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false
          })
          
          // Force redirect หลังจาก 3 วินาที
          setTimeout(() => {
            window.location.href = '/'
          }, 3000)
        }
      }
    } catch (error) {
      console.error('❌ Logout confirmation error:', error)
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถออกจากระบบได้ กรุณาลองใหม่',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    }
  }

  return (
    <>
      {/* Session Warning Modal — เตือนก่อน idle หมดเวลา แล้ว logout อัตโนมัติ */}
      <SessionWarningModal
        isOpen={showSessionWarning}
        timeRemaining={timeRemaining}
        onExtendSession={extendSession}
        onLogout={sessionTimeoutLogout}
      />

      {/* Single-tab overlay: กันหลายแท็บ — แท็บที่เปิดทีหลังจะถูกบล็อก */}
      {isDuplicateTab && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 420 }}>
            <p style={{ fontSize: '1.25rem', marginBottom: 16, fontWeight: 600 }}>
              มีการใช้งานจากอีกแท็บหนึ่งอยู่
            </p>
            <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: 24 }}>
              เพื่อความปลอดภัย กรุณาใช้แค่หนึ่งแท็บขณะเล่นหรือถอนเงิน
              หากแท็บอื่นปิดไปแล้ว หรือต้องการใช้แท็บนี้แทน กดปุ่มด้านล่าง
            </p>
            <button
              type="button"
              onClick={takeOverSession}
              className="btn btn-primary btn-lg"
              style={{ padding: '12px 24px' }}
            >
              ใช้แท็บนี้แทน
            </button>
          </div>
        </div>
      )}

      {/* Header for authenticated users */}
      <header className="header">
        <div className="container">
          <div className="wrap flex-nowrap justify-content-between">
            <Link to="/dashboard" className="logo-d py-2">
              <img src={branding.logoUrl} alt="" onError={(e) => {
                (e.target as HTMLImageElement).src = getAssetUrl('/img/logo.png')
              }} />
            </Link>
            <div className="wrap-d">
              <div className="wrap justify-content-end">
                <Link to="/dashboard" className="txt-link-d fs-14 m-none">
                  <i className="fas fa-home me-1"></i>
                  หน้าหลัก
                </Link>
                <div className="line-between m-none"></div>
                <Link to="/lottery-result" className="txt-link-d fs-14 m-none">
                  <i className="fas fa-trophy me-1"></i>
                  ผลรางวัล
                </Link>
                <div className="line-between m-none"></div>
                <a
                  href="#"
                  className="txt-link-d fs-14 m-none"
                  onClick={(e) => {
                    e.preventDefault()
                    if (isLoadingUserInfo) {
                      navigate('/topup')
                      return
                    }
                    const hasBankAccount = getPrimaryBankAccount() != null
                    if (!hasBankAccount) {
                      navigate('/profile')
                    } else {
                      navigate('/topup')
                    }
                  }}
                >
                  <i className="fas fa-credit-card me-1"></i>
                  เติมเงิน
                </a>
                <div className="line-between m-none"></div>
                <Link to="/financial-report" className="txt-link-d fs-14">
                  <i className="fas fa-chart-bar me-1"></i>
                  {userData.balance}
                </Link>
                <div className="line-between m-none"></div>
                <a
                  href="#"
                  className="button-main bgc-4 fs-14 c-red t-regular py-0 m-none"
                  style={{ minWidth: 'auto' }}
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="fab fa-line"></i>
                  ติดต่อเรา (ไลน์)
                </a>
                <div className="line-between"></div>
                <a 
                  className="txt-link-d fs-14" 
                  href="#" 
                  id="MenuList"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowUserMenu(!showUserMenu)
                  }}
                >
                  <i className="fas fa-user-circle me-1"></i>
                  ผู้ใช้
                  <i className="fas fa-chevron-down ms-1"></i>
                </a>
              </div>
              <div className="wrap-noti bgc-4 bdr-24 px-2 mt-4">
                <marquee
                  direction="left"
                  onMouseOver={(e: React.MouseEvent<HTMLElement>) => (e.target as any).stop()}
                  onMouseOut={(e: React.MouseEvent<HTMLElement>) => (e.target as any).start()}
                >
                  {marqueeText || 'ยินดีต้อนรับทุกท่านเข้าสู่เว็บ lenhuay.net เว็บหวยออนไลน์ที่มาแรงที่สุดในตอนนี้ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย'}
                </marquee>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* User Menu Dropdown - ตรงตาม HTML ต้นฉบับ */}
      <div 
        className={`menu-list bgc-15 ${showUserMenu ? 'slide' : ''}`}
        style={{ 
          display: showUserMenu ? 'block' : 'none',
          position: 'fixed',
          top: '0',
          right: '0',
          width: '320px',
          height: '100vh',
          zIndex: 9999,
          overflow: 'auto'
        }}
      >
        <div className="close-menu" onClick={() => setShowUserMenu(false)}>
          <i className="fas fa-times"></i>
        </div>
        <div className="text-center border-bottom-m c-white p-2">
          <h1><i className="fas fa-user-circle"></i></h1>
          <h6 className="">{userData.username}</h6>
          <h6 className="t-regular">{userData.balance}</h6>
        </div>

        <ul className="bdr-5 py-4 px-3 m-0">
          <li>
            <Link 
              className="txt-link-m border-bottom-m py-2" 
              to="/profile"
              onClick={() => setShowUserMenu(false)}
            >
              <i className="fas fa-cog me-2"></i>สรุปข้อมูลของคุณ
            </Link>
          </li>
          <li>
            <Link 
              className="txt-link-m border-bottom-m py-2" 
              to="/change-password"
              onClick={() => setShowUserMenu(false)}
            >
              <i className="fas fa-key me-2"></i>เปลี่ยนรหัสผ่าน
            </Link>
          </li>
          <li>
            <Link 
              className="txt-link-m border-bottom-m py-2" 
              to="/contact"
              onClick={() => setShowUserMenu(false)}
            >
              <i className="fas fa-comments me-2"></i>ศูนย์ช่วยเหลือ
            </Link>
          </li>
        </ul>
        <div className="p-1">
          <button 
            className="check-button bgg-2 bdr-5 text-center c-red t-medium w-100 border-0"
            onClick={handleLogout}
          >
            ออกจากระบบ
          </button>
        </div>
      </div>

      {/* Overlay - ตรงตาม HTML ต้นฉบับ */}
      <div 
        className="overlay"
        style={{ display: showUserMenu ? 'block' : 'none' }}
        onClick={() => setShowUserMenu(false)}
      ></div>

      <a id="button" className=""></a>
      <div className="bg-fixed"></div>

      {/* Main Content */}
      {children}

      {/* Footer */}
      <footer className="footer c-red text-center py-2 mb-4 animateMe" data-animation="fadeInUp">
        <small>{branding.footerText || `Copyright © 2024 ${branding.siteName}. All rights reserved.`}</small>
      </footer>

      {/* Chat Widget - Only when webChat is enabled and user is authenticated */}
      {isAuthenticated && themeConfig.settings?.webChatEnabled !== false && (
        <ChatWidget themeConfig={themeConfig} />
      )}
    </>
  )
}

export default AuthenticatedLayout
